import { PartialRequired } from '@shared/utils/utilities';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '@app/services/error.service';
import { toParams } from '@shared/utils/query-to-params';
import { Cell, embed as cellEmbed, configuration as cellConfiguration, EmbedCell } from '@data/models/cell.model';
import { Embedded } from '@data/models/embed.model';
import { uniq } from 'lodash';
import { MetadataService } from './metadata.service';
import { ListMetadataKeys, MetadataLine } from '@data/models/metadata.model';
import { AnalyticsCodes } from '@data/models/tribe.model';
import { FilterParams } from '@claranet/node-search-filter';

export const CELL_SERVICE_API_ROOT = '/core/v1/cells';

@Injectable()
export class CellService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private metadataService: MetadataService,
    ) {
        this.errors = this.errorService.cellServiceErrors;
    }

    /**
     * Get sorted cells list
     *
     * @returns {Observable<Cell[]>} - The cell list
     *
     * @memberof CellService
     */
    public findCells(filterParams?: FilterParams): Observable<Cell[]> {
        const params = toParams(filterParams);
        return this.http.get<{ cells: Cell[] }>(CELL_SERVICE_API_ROOT, { params }).pipe(
            map((result) => result.cells),
            catchError(this.errorService.errorHandler<Cell[]>(this.errors, 'Récupération des cellules', []))
        );
    }

    public getCell(cellId: string): Observable<Cell> {
        return this.http.get<Cell>(CELL_SERVICE_API_ROOT + '/' + cellId).pipe(
            catchError(this.errorService.errorHandler<Cell>(this.errors, 'Récupération des données de cellule', null))
        );
    }

    public getEmbeddedCell<T extends cellEmbed>(cellId: string, embed: T[] = []): Observable<EmbedCell<T>> {
        const embeds = uniq(embed.map((it) => cellConfiguration[it].embed)).join(',');
        return this.http.get<Cell & { embedded: Embedded }>(CELL_SERVICE_API_ROOT + '/' + cellId, { params: { embed: embeds } }).pipe(
            map((cell) => Cell.toCustomCell(cell, cell.embedded, embed)),
            catchError(this.errorService.errorHandler<EmbedCell<T>>(this.errors, 'Récupération des données de cellule', null))
        );
    }

    public findEmbeddedCell<T extends cellEmbed>(filterParams?: FilterParams, embed: T[] = []): Observable<EmbedCell<T>[]> {
        const embeds = uniq(embed.map((it) => cellConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds, ...filterParams });
        return this.http.get<{ cells: Cell[]; embedded: Embedded }>(CELL_SERVICE_API_ROOT, { params }).pipe(
            map(({ cells, embedded }) => cells.map(cell => Cell.toCustomCell(cell, embedded, embed))),
            catchError(this.errorService.errorHandler<EmbedCell<T>[]>(this.errors, 'Récupération des données de cellule', null))
        );
    }

    public updateMetadata(cellId: string, { key, value }: MetadataLine): Observable<Cell> {
        return this.http.put<Cell>(`${CELL_SERVICE_API_ROOT}/${cellId}/metadata/${encodeURIComponent(key)}`, { value }).pipe(
            map((cell) => new Cell(cell)),
            catchError(this.errorService.errorHandler<Cell>(this.errors, 'Modification des metadatas de la cellule', null))
        );
    }

    public deleteMetadata(cellId: string, key: string): Observable<Cell> {
        return this.http.delete<Cell>(`${CELL_SERVICE_API_ROOT}/${cellId}/metadata/${encodeURIComponent(key)}`).pipe(
            map((cell) => new Cell(cell)),
            catchError(this.errorService.errorHandler<Cell>(this.errors, 'Suppression d\'une metadata de la cellule', null))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(CELL_SERVICE_API_ROOT, filterParams, this.errors);
    }

    public fetchAnalyticsCodes(filterParams: FilterParams): Observable<AnalyticsCodes> {
        const params = toParams(filterParams);
        return this.http.get<{ analyticsCodes: AnalyticsCodes }>(`${CELL_SERVICE_API_ROOT}/analytics-codes`, { params }).pipe(
            map((result) => result.analyticsCodes),
            catchError(this.errorService.errorHandler<AnalyticsCodes>(this.errors, 'Récupération des analytics codes des cellules', null))
        );
    }

    public updateCell(cell: PartialRequired<Cell, 'id'>): Observable<Cell> {
        return this.http.patch<Cell>(`${CELL_SERVICE_API_ROOT}/${cell.id}`, cell).pipe(
            map((result) => new Cell(result)),
            catchError(this.errorService.errorHandler<Cell>(this.errors, 'Modification des metadatas de la cellule', null))
        );
    }

}
